// frontend/src/components/DegreeAudit/RequirementDisplays/AuditRequirementDisplay.tsx

import { memo, useMemo } from 'react';
import { Check, Clock, Circle } from 'lucide-react';

import {
  COURSE_STATUS_COMPLETED,
  COURSE_STATUS_IN_PROGRESS,
  COURSE_STATUS_PLANNED,
} from '../../../utils/constants';
import RequirementOverrideSlot from '../Overrides/RequirementOverrideSlot';
import { useCreditUnit } from '../../../contexts/InstitutionConfigContext';

interface AuditCourse {
  code?: string;
  title?: string;
  status?: string;
  semester?: string;
  semester_code?: string;
  term?: string;
  year?: string | number;
  [key: string]: unknown;
}

interface AreaProgressEntry {
  completed?: number;
  in_progress?: number;
  planned?: number;
}

interface AuditRequirementMetadata {
  description?: string | null;
  filter_display?: string | null;
  dynamic_matches?: unknown[];
  [key: string]: unknown;
}

interface AuditRequirementShape {
  type?: string;
  description?: string | null;
  filter_display?: string | null;
  is_credit_based?: boolean;
  credits_required?: number;
  credits_completed?: number;
  credits_in_progress?: number;
  required?: number;
  courses_required?: number;
  completed_count?: number;
  completed?: number;
  in_progress_count?: number;
  planned_count?: number;
  progress_percentage?: number;
  pattern?: string;
  areas_required?: number;
  areas_with_progress?: number;
  area_progress?: Record<string, AreaProgressEntry>;
  available_courses?: Array<string | AuditCourse>;
  courses?: AuditCourse[];
  code?: string;
  // #559: catalog leaves this requirement to the advisor; no course list.
  advisor_determined?: boolean;
  [key: string]: unknown;
}

export interface AuditRequirementNode {
  name?: string;
  requirement?: AuditRequirementShape;
  metadata?: AuditRequirementMetadata;
  [key: string]: unknown;
}

export interface AuditRequirementDisplayProps {
  requirement: AuditRequirementNode;
}

interface CategorizedCourses {
  completed: AuditCourse[];
  inProgress: AuditCourse[];
  planned: AuditCourse[];
  unmatched: AuditCourse[];
}

/**
 * Simplified audit view for requirements display.
 *
 *  - Met: shows checkmark + which course(s) satisfied it
 *  - In Progress: shows clock + completed courses + remaining needed
 *  - Unmet: shows circle + available course options or description
 */
const AuditRequirementDisplay = memo(({ requirement }: AuditRequirementDisplayProps) => {
  const creditUnit = useCreditUnit();
  const req: AuditRequirementShape = requirement.requirement || {};
  const metadata: AuditRequirementMetadata = requirement.metadata || {};

  const progressPercentage = req.progress_percentage || 0;
  const isComplete = progressPercentage >= 100;
  const hasProgress = progressPercentage > 0 && progressPercentage < 100;

  const isCreditBased = req.is_credit_based;

  const requiredCount = isCreditBased
    ? req.credits_required || 1
    : req.required || req.courses_required || 1;
  const completedCount = req.completed_count || req.completed || 0;
  const inProgressCount = req.in_progress_count || 0;
  const plannedCount = req.planned_count || 0;

  const isChooseN = req.type === 'choose' || req.type === 'distribution' || requiredCount > 1;
  const isPattern = req.type === 'pattern';
  const isDistribution = req.type === 'distribution';
  const isDynamicMatch =
    req.type === 'dynamic_match' || (metadata.dynamic_matches?.length ?? 0) > 0;

  const areasRequired = req.areas_required || 0;
  const areasWithProgress = req.areas_with_progress || 0;
  const areaProgress = req.area_progress || {};

  const availableCourses = req.available_courses || [];

  const filterDisplay = req.filter_display || metadata.filter_display || null;
  const description = req.description || filterDisplay || metadata.description || null;

  const categorizedCourses: CategorizedCourses = useMemo(() => {
    const courses = req.courses || [];
    const completed: AuditCourse[] = [];
    const inProgress: AuditCourse[] = [];
    const planned: AuditCourse[] = [];
    const unmatched: AuditCourse[] = [];

    courses.forEach((course) => {
      const status = course.status || COURSE_STATUS_PLANNED;
      if (status === COURSE_STATUS_COMPLETED) {
        completed.push(course);
      } else if (status === COURSE_STATUS_IN_PROGRESS) {
        inProgress.push(course);
      } else if (status === COURSE_STATUS_PLANNED) {
        planned.push(course);
      } else {
        unmatched.push(course);
      }
    });

    const sortBySemester = (a: AuditCourse, b: AuditCourse): number => {
      const aCode = parseInt(a.semester_code || '999', 10);
      const bCode = parseInt(b.semester_code || '999', 10);
      return aCode - bCode;
    };

    completed.sort(sortBySemester);
    inProgress.sort(sortBySemester);
    planned.sort(sortBySemester);

    return { completed, inProgress, planned, unmatched };
  }, [req.courses]);

  const formatSemester = (course: AuditCourse): string | null => {
    if (course.semester) return course.semester;
    if (course.term && course.year) return `${course.term} ${course.year}`;
    return null;
  };

  const SatisfiedCourses = (): JSX.Element | null => {
    const { completed, inProgress, planned } = categorizedCourses;
    const satisfyingCourses = [...completed, ...inProgress];

    if (satisfyingCourses.length === 0) return null;

    return (
      <div className="mt-1 space-y-0.5">
        {satisfyingCourses.map((course, idx) => {
          const semester = formatSemester(course);
          const isInProg = course.status === COURSE_STATUS_IN_PROGRESS;
          return (
            <div key={course.code || idx} className="flex items-center gap-1.5 text-sm">
              <Check
                className={`w-3.5 h-3.5 flex-shrink-0 ${isInProg ? 'text-blue-500' : 'text-green-500'}`}
              />
              <span className="font-medium text-gray-800">{course.code}</span>
              {course.title && <span className="text-gray-500 truncate">- {course.title}</span>}
              {semester && (
                <span className="text-gray-400 text-xs">
                  ({semester}
                  {isInProg ? ', in progress' : ''})
                </span>
              )}
            </div>
          );
        })}
        {planned.length > 0 && (
          <div className="text-sm text-yellow-700 mt-1">
            Planned: {planned.map((c) => c.code).join(', ')}
          </div>
        )}
      </div>
    );
  };

  const ProgressSummary = (): JSX.Element | null => {
    if (isComplete) return null;

    const unit = isCreditBased ? ` ${creditUnit.plural}` : '';
    const remaining = isCreditBased
      ? Math.max(
          0,
          requiredCount - (req.credits_completed || 0) - (req.credits_in_progress || 0)
        )
      : Math.max(0, requiredCount - completedCount - inProgressCount);

    if (completedCount === 0 && inProgressCount === 0 && plannedCount === 0 && remaining === 0) {
      return null;
    }

    return (
      <div className="mt-1">
        <div className="text-sm">
          {completedCount > 0 && (
            <span className="text-green-700">{completedCount} completed</span>
          )}
          {inProgressCount > 0 && (
            <span className="text-blue-700">
              {completedCount > 0 ? ', ' : ''}
              {inProgressCount} in progress
            </span>
          )}
          {plannedCount > 0 && (
            <span className="text-yellow-700">
              {completedCount > 0 || inProgressCount > 0 ? ', ' : ''}
              {plannedCount} planned
            </span>
          )}
          {remaining > 0 && (
            <span className="text-gray-500">
              {completedCount > 0 || inProgressCount > 0 || plannedCount > 0 ? ' · ' : ''}
              {remaining}
              {unit} more needed
            </span>
          )}
        </div>
      </div>
    );
  };

  const AreaProgressDisplay = (): JSX.Element | null => {
    if (!isDistribution || areasRequired === 0) return null;

    const areaNames = Object.keys(areaProgress);
    const areasNeeded = Math.max(0, areasRequired - areasWithProgress);

    const formatAreaName = (name: string): string =>
      name
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    return (
      <div className="mt-2 space-y-1">
        <div className="text-sm">
          <span
            className={areasWithProgress >= areasRequired ? 'text-green-700' : 'text-gray-700'}
          >
            {areasWithProgress} of {areasRequired} areas
          </span>
          {areasNeeded > 0 && (
            <span className="text-gray-500">
              {' '}
              · {areasNeeded} more area{areasNeeded > 1 ? 's' : ''} needed
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-1">
          {areaNames.map((areaName) => {
            const area = areaProgress[areaName] || {};
            const completed = area.completed || 0;
            const inProgress = area.in_progress || 0;
            const planned = area.planned || 0;
            const hasCompletedOrInProgress = completed > 0 || inProgress > 0;
            const courseCount = completed + inProgress;

            return (
              <span
                key={areaName}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${
                  hasCompletedOrInProgress
                    ? 'bg-green-100 text-green-800'
                    : planned > 0
                      ? 'bg-yellow-50 text-yellow-700'
                      : 'bg-gray-100 text-gray-500'
                }`}
                title={`${formatAreaName(areaName)}: ${completed} completed, ${inProgress} in progress, ${planned} planned`}
              >
                {hasCompletedOrInProgress && <Check className="w-3 h-3" />}
                {formatAreaName(areaName)}
                {courseCount > 0 && <span className="text-xs opacity-75">({courseCount})</span>}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const AvailableOptions = (): JSX.Element | null => {
    if (isComplete) return null;

    if (isPattern && description) {
      return (
        <div className="mt-2">
          <div className="text-sm text-gray-600 bg-gray-50 rounded px-2 py-1.5">
            <span className="text-gray-500">Requirement: </span>
            {description}
          </div>
        </div>
      );
    }

    if (availableCourses.length > 0) {
      const plannedCourses = availableCourses.filter(
        (c): c is AuditCourse => typeof c === 'object' && c.status === COURSE_STATUS_PLANNED
      );
      const choosableCourses = availableCourses.filter(
        (c) => typeof c === 'string' || !('status' in c) || c.status !== COURSE_STATUS_PLANNED
      );

      const displayLimit = 8;
      const displayChoosable = choosableCourses.slice(0, displayLimit);
      const hasMore = choosableCourses.length > displayLimit;

      return (
        <div className="mt-2">
          {plannedCourses.length > 0 && (
            <div className="mb-1.5">
              <div className="text-xs text-gray-500 mb-1">Planned:</div>
              <div className="flex flex-wrap gap-1.5">
                {plannedCourses.map((course, idx) => (
                  <span
                    key={course.code || idx}
                    className="inline-flex items-center px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded"
                    title={course.title || course.code}
                  >
                    {course.code}
                  </span>
                ))}
              </div>
            </div>
          )}
          {displayChoosable.length > 0 && (
            <>
              <div className="text-xs text-gray-500 mb-1">Choose from:</div>
              <div className="flex flex-wrap gap-1.5">
                {displayChoosable.map((course, idx) => {
                  const code = typeof course === 'string' ? course : course.code;
                  const title = typeof course === 'object' ? course.title : null;

                  return (
                    <span
                      key={code || idx}
                      className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                      title={title || code}
                    >
                      {code}
                    </span>
                  );
                })}
                {hasMore && (
                  <span className="inline-flex items-center px-2 py-0.5 text-gray-500 text-xs">
                    +{choosableCourses.length - displayLimit} more
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      );
    }

    if (description) {
      return (
        <div className="mt-2">
          <div className="text-sm text-gray-500 italic">{description}</div>
        </div>
      );
    }

    return null;
  };

  const getSubtitle = (): string | null => {
    if (isComplete) {
      return null;
    }

    if (isChooseN && requiredCount > 1) {
      if (isCreditBased) {
        return `Select ${creditUnit.format(req.credits_required || requiredCount)}`;
      }
      return `Select ${requiredCount} course${requiredCount > 1 ? 's' : ''}`;
    }

    if (isPattern && req.pattern) {
      return `Pattern: ${req.pattern}`;
    }

    if (isDynamicMatch && filterDisplay) {
      return filterDisplay;
    }

    return null;
  };

  // Reserved for future use; the StatusIcon and getDisplayTitle helpers exist
  // in the original component but the current render path doesn't use them.
  void Clock;
  void Circle;

  const subtitle = getSubtitle();

  // #559: nothing to list and nothing to count — say why, and keep the
  // override control (a student whose advisor signed off records it there).
  if (req.advisor_determined === true) {
    return (
      <div className="py-1">
        <div className="text-sm text-ink-2" data-testid="advisor-determined-note">
          Your advisor determines how this requirement is met. The planner can&apos;t check it, so
          it counts neither for nor against your progress. If your advisor has approved it, you
          can record that as an override.
        </div>
        <RequirementOverrideSlot code={req.code} name={requirement.name} isSatisfied={false} />
      </div>
    );
  }

  return (
    <div className="py-1">
      {subtitle && <div className="text-xs text-gray-500 mb-1">{subtitle}</div>}

      {isComplete && (
        <>
          <SatisfiedCourses />
          <AreaProgressDisplay />
        </>
      )}

      {hasProgress && (
        <>
          <SatisfiedCourses />
          <ProgressSummary />
          <AreaProgressDisplay />
          <AvailableOptions />
        </>
      )}

      {!isComplete && !hasProgress && (
        <>
          <ProgressSummary />
          <AreaProgressDisplay />
          <AvailableOptions />
        </>
      )}

      <RequirementOverrideSlot code={req.code} name={requirement.name} isSatisfied={isComplete} />
    </div>
  );
});

AuditRequirementDisplay.displayName = 'AuditRequirementDisplay';

export default AuditRequirementDisplay;
